import { ExternalLink, Plus } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api/rawFetch'
import type { components } from '@/lib/api/generated'
import type { POFormValues, POLineItemFormValues } from '@/lib/schemas/po'
import { signalKind } from '@/hooks/useDashboard'

type DashboardRow = components['schemas']['DashboardRow']
// ProductDetailResponse extends DashboardRow with order_history; we only need
// the dashboard fields for this modal.
type ProductDetailResponse = DashboardRow

const SHOPIFY_SHOP = (import.meta.env.VITE_SHOPIFY_SHOP as string | undefined)?.trim() || ''

function shopifyProductUrl(productId: number | string | null | undefined): string | null {
  if (!SHOPIFY_SHOP || productId == null || productId === '') return null
  return `https://${SHOPIFY_SHOP}/admin/products/${productId}`
}

function fmtNum(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

// Mirrors ProductSearchPicker.handlePick — same mapping the wizard uses when
// a user picks a product via search, so newly-added related variants are
// indistinguishable from search-added ones downstream.
function variantToLineItem(p: DashboardRow): POLineItemFormValues | null {
  const variantId = Number(p.variant_id ?? 0)
  const productId = Number(p.product_id ?? 0)
  if (!variantId || !productId) return null
  return {
    variant_id: variantId,
    product_id: productId,
    product_title: p.product_title ?? '',
    variant_title: p.variant_title ?? '',
    sku: p.sku ?? '',
    barcode: p.barcode ?? '',
    vendor: p.vendor ?? '',
    retail_price: 0,
    cost_base: p.unit_cost ?? 0,
    // Treat 0 as "no recommendation" — same convention as the other add paths
    // (ProductSearchPicker, prefill card, dashboard-prefill mapper). Otherwise a
    // rec_qty of 0 (the backend returns 0 when on_hand + incoming already
    // cover predicted demand) would seed the line at qty 0, which the user
    // can never order.
    qty_ordered: p.recommended_qty || 1,
    notes: '',
    on_hand: p.on_hand ?? 0,
    incoming: p.incoming ?? 0,
    rec_qty: p.recommended_qty ?? undefined,
    signal: signalKind(p.action_signal),
    return_rate: p.return_rate ?? undefined,
  }
}

function useProductDetail(variantId: number | null) {
  return useQuery({
    queryKey: ['product-detail', variantId] as const,
    queryFn: () => apiFetch<ProductDetailResponse>('GET', `/api/inventory/products/${variantId}`),
    enabled: variantId != null,
    staleTime: 60_000,
  })
}

function useRelatedVariants(productId: number | null) {
  return useQuery({
    queryKey: ['product-variants', productId] as const,
    queryFn: () =>
      apiFetch<DashboardRow[]>('GET', '/api/inventory/products', {
        query: { product_id: productId!, limit: 200 },
      }),
    enabled: productId != null,
    staleTime: 60_000,
  })
}

export function ItemDetailsModal({
  open,
  onOpenChange,
  index,
  onAddItem,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  index: number | null
  // StepItems owns the canonical useFieldArray for `items`; mounting a second
  // one here desyncs the wizard table. Take append as a prop instead.
  onAddItem: (item: POLineItemFormValues) => void
}) {
  const { control } = useFormContext<POFormValues>()
  const items = useWatch({ control, name: 'items' }) ?? []
  const row = index != null ? items[index] : undefined

  const variantId = row?.variant_id ?? null
  const productId = row?.product_id ?? null
  const { data: detail, isLoading: detailLoading } = useProductDetail(variantId)
  const { data: variants, isLoading: variantsLoading } = useRelatedVariants(productId)

  const addedVariantIds = new Set(items.map((i) => i.variant_id))
  const shopifyHref = shopifyProductUrl(productId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] max-w-4xl">
        <DialogHeader>
          <DialogTitle>Item Details</DialogTitle>
        </DialogHeader>

        {row && (
          <>
            <div className="text-sm">
              <span className="font-semibold">{row.product_title}</span>
              {row.variant_title && <> — {row.variant_title}</>}
            </div>

            {/* Related Variants */}
            <section className="border border-[var(--line-warm)]">
              <header className="bg-[var(--surface-header)] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">
                Related Variants
              </header>
              <div className="max-h-56 overflow-auto">
                {variantsLoading && (
                  <div className="p-2 space-y-1">
                    {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}
                  </div>
                )}
                {!variantsLoading && variants && variants.length > 0 && (
                  <table className="w-full text-xs">
                    <tbody>
                      {variants.map((v) => {
                        const added = v.variant_id != null && addedVariantIds.has(Number(v.variant_id))
                        const isCurrent = v.variant_id != null && Number(v.variant_id) === variantId
                        return (
                          <tr key={String(v.variant_id)} className={`border-b border-[var(--line-warm)] last:border-b-0 ${isCurrent ? 'bg-[var(--surface-page)]' : ''}`}>
                            <td className="px-3 py-1.5 font-medium">{v.variant_title || '—'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-[var(--ink-2)]">Stock: {fmtNum(v.on_hand)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-[var(--ink-2)]">30D: {fmtNum(v.qty_sold_30d)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-[var(--ink-2)]">60D: {fmtNum(v.qty_sold_60d)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-[var(--ink-2)]">{fmtCurrency(v.unit_cost)}</td>
                            <td className="px-2 py-1 text-right w-20">
                              {added ? (
                                <span className="text-[var(--brand)] font-medium">Added</span>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    const item = variantToLineItem(v)
                                    if (item) onAddItem(item)
                                  }}
                                >
                                  <Plus className="mr-0.5 h-3 w-3" />
                                  Add
                                </Button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
                {!variantsLoading && (!variants || variants.length === 0) && (
                  <div className="p-3 text-xs text-[var(--mute)]">No related variants.</div>
                )}
              </div>
            </section>

            {/* Analysis */}
            <section className="border border-[var(--line-warm)]">
              <header className="bg-[var(--surface-header)] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">
                Analysis
              </header>
              <div className="p-3">
                {detailLoading && <Skeleton className="h-12 w-full" />}
                {!detailLoading && detail && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[var(--mute)]">
                        <th className="tbl-th text-right">Days Coverage</th>
                        <th className="tbl-th text-right">Velocity</th>
                        <th className="tbl-th text-right">Pred Demand</th>
                        <th className="tbl-th text-right">30D Sold</th>
                        <th className="tbl-th text-right">60D Sold</th>
                        <th className="tbl-th text-right">Incoming</th>
                        <th className="tbl-th text-right">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.days_coverage, 0)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.true_velocity, 3)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.predicted_demand, 3)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.qty_sold_30d)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.qty_sold_60d)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.incoming)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtNum(detail.on_hand)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <div className="flex justify-start">
              {shopifyHref ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={shopifyHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-1 h-3 w-3" />
                    Open in Shopify Admin
                  </a>
                </Button>
              ) : (
                <span className="text-xs text-[var(--mute)]">Shopify admin link unavailable (VITE_SHOPIFY_SHOP not set)</span>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
